import React from "react";
import {useParams} from "react-router-dom";
import Layout from "../../common/Layout";
import FooterOne from "../../common/footer/FooterOne";
import {Helmet} from "react-helmet";

const ORANGE = "#F2B34C"; // Happy Solutions orange

// API: Only this endpoint is used per spec
async function fetchOrderDetails(orderId) {
  const url = `https://api.happygastro.hu/api/order/${orderId}`;
  const res = await fetch(url, { credentials: "omit" });
  if (!res.ok) {
    let msg = "Order not found or API unavailable.";
    try {
      const maybe = await res.json();
      if (maybe && (maybe.message || maybe.error)) {
        msg = maybe.message || maybe.error;
      }
    } catch (e) {}
    throw new Error(msg);
  }
  const data = await res.json();
  return { data, url };
}

const Row = ({ label, value }) => (
  <div style={{ display: "flex", gap: 12, padding: "6px 0", fontSize: 14 }}>
    <div style={{ width: 160, color: "#555" }}>{label}</div>
    <div style={{ flex: 1, fontWeight: 600 }}>{value}</div>
  </div>
);

const Address = ({ title, addr }) => {
  if (!addr) return null;
  const parts = [
    addr.name || addr.fullName,
    [addr.postalCode, addr.city].filter(Boolean).join(" "),
    [addr.address1, addr.address2].filter(Boolean).join(" "),
    addr.country,
    addr.phone,
  ].filter(Boolean);
  return (
    <div style={{ padding: 16, border: "1px solid #eee", borderRadius: 8 }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ whiteSpace: "pre-line", lineHeight: 1.4 }}>
        {parts.join("\n") || "—"}
      </div>
    </div>
  );
};

const Price = ({ value, currency = "HUF", valueAligned = false }) => {
  if (value === undefined || value === null || isNaN(Number(value))) return <>—</>;
  return <span style={valueAligned ? {float: "right"} : {}}>{new Intl.NumberFormat("en-US").format(Number(value))} {currency}</span>;
};

const ItemRow = ({ item, currency }) => {
  const name = item?.name || item?.title || "Item";
  const qty = item?.amount || item?.qty || item?.quantity || 1;
  const unitPrice = item?.price || item?.unitPrice || item?.unit_price;
  const total = item?.lineTotal || item?.line_total || item?.total || (unitPrice ? unitPrice * qty : undefined);
  const photo = item?.item?.photo || item?.photo || item?.image || item?.imageUrl;
  return (
    <tr>
      <td style={{ padding: "10px 12px", borderBottom: "1px solid #eee" }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {photo ? (
            <img src={photo} alt={name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} />
          ) : null}
          <div>{name}</div>
        </div>
      </td>
      <td style={{ padding: "10px 12px", borderBottom: "1px solid #eee", textAlign: "center" }}>{qty}</td>
      <td style={{ padding: "10px 12px", borderBottom: "1px solid #eee", textAlign: "right" }}><Price value={unitPrice} currency={currency} /></td>
      <td style={{ padding: "10px 12px", borderBottom: "1px solid #eee", textAlign: "right" }}><Price value={total} currency={currency} /></td>
    </tr>
  );
};

const Pill = ({ children, color = "#111", bg = "rgba(0,0,0,0.06)", borderColor }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      color,
      background: bg,
      border: `1px solid ${borderColor || "rgba(0,0,0,0.15)"}`,
    }}
  >
    {children}
  </span>
);

// Small helper to render an icon. If icon exists, show only the logo (bigger); otherwise show text.
// Falls back to text if the image fails to load.
const IconText = ({ icon, text }) => {
  const [failed, setFailed] = React.useState(false);
  if (icon && !failed) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <img src={icon} alt={text} title={text} style={{ height: 28, width: "auto", objectFit: "contain" }} onError={() => setFailed(true)} />
      </span>
    );
  }
  return <span>{text}</span>;
};

// Map known platforms and couriers to brand icons (served from Clearbit logo CDN for reliability)
const PLATFORM_ICONS = {
  shopify: "https://logo.clearbit.com/shopify.com",
  amazon: "https://logo.clearbit.com/amazon.com",
  allegro: "https://logo.clearbit.com/allegro.pl",
  emag: "https://logo.clearbit.com/emag.hu",
};

const COURIER_ICONS = {
  gls: "https://logo.clearbit.com/gls-group.eu",
  ups: "https://logo.clearbit.com/ups.com",
};

function getPlatformInfo(val) {
  const raw = String(val || "").trim();
  const key = raw.toLowerCase();
  if (key.includes("shopify")) return { text: "Shopify", icon: PLATFORM_ICONS.shopify };
  if (key.includes("amazon")) return { text: "Amazon", icon: PLATFORM_ICONS.amazon };
  if (key.includes("allegro")) return { text: "Allegro", icon: PLATFORM_ICONS.allegro };
  if (key === "emag" || key.includes("e-mag") || key.includes("emag")) return { text: "eMAG", icon: PLATFORM_ICONS.emag };
  return { text: raw, icon: null };
}

function getCourierInfo(val) {
  const raw = String(val || "").trim();
  const key = raw.toLowerCase();
  if (key.includes("gls")) return { text: "GLS", icon: COURIER_ICONS.gls };
  if (key.includes("ups")) return { text: "UPS", icon: COURIER_ICONS.ups };
  return { text: raw, icon: null };
}

const CARD = {
  padding: 16,
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 10,
  background: 'linear-gradient(180deg, #fff, #fffdf7)',
  boxShadow: '0 6px 18px rgba(0,0,0,0.06)'
};
const CARD_TITLE = { fontWeight: 800, marginBottom: 8 };

export default function OrderTracking() {
  const { orderId } = useParams();
  const [state, setState] = React.useState({ loading: true, error: "", order: null, sourceUrl: "" });

  React.useEffect(() => {
    let mounted = true;
    setState(s => ({ ...s, loading: true, error: "", order: null }));
    fetchOrderDetails(orderId)
      .then(({ data, url }) => {
        if (!mounted) return;
        setState({ loading: false, error: "", order: data, sourceUrl: url });
      })
      .catch((err) => {
        if (!mounted) return;
        setState({ loading: false, error: err?.message || "Failed to load order.", order: null, sourceUrl: "" });
      });
    return () => { mounted = false; };
  }, [orderId]);

  const { loading, error, order } = state;

  // Try to normalize some well-known fields
  const currency = order?.currency || order?.currencyCode || "HUF";
  const orderNumber = order?.orderNumber || order?.number || order?._id || order?.id || orderId;
  const displayOrderCode = order?.orderCode || orderNumber;
  const status = order?.status || order?.financialStatus || order?.fulfillmentStatus || "—";
  const createdAt = order?.createdAt || order?.created_at || order?.created || order?.date || null;
  const paymentMethod = order?.paymentMethod || order?.payment?.method || order?.payment_type || order?.paymentType || "—";
  const items = order?.items || order?.lines || order?.line_items || [];

  // Page title: set after order loads
  const initialTitleRef = React.useRef(document.title);
  React.useEffect(() => {
    if (order && displayOrderCode) {
      document.title = `Order ${displayOrderCode} - Happy Solutions`;
    }
  }, [order, displayOrderCode]);
  React.useEffect(() => {
    return () => { document.title = initialTitleRef.current; };
  }, []);
  // Calculated totals
  const itemsSubtotal = Array.isArray(items)
    ? items.reduce((sum, it) => {
        const q = Number(it?.amount ?? it?.qty ?? it?.quantity ?? 1);
        const p = Number(it?.price ?? it?.unitPrice ?? it?.unit_price ?? 0);
        return sum + (isNaN(q * p) ? 0 : q * p);
      }, 0)
    : 0;
  const rawDiscounts = [
    order?.discountAmount,
    order?.discountsAmount,
    order?.discountPrice,
    order?.discount?.amount,
    order?.discounts?.amount,
    order?.discountsTotal
  ].filter(v => typeof v === "number" && !isNaN(v));
  const discount = rawDiscounts.length ? rawDiscounts.reduce((a, b) => a + Number(b || 0), 0) : 0;
  const subtotal = itemsSubtotal - discount;
  const shipping =
    order?.deliveryFee ??
    order?.deliveryPrice ??
    order?.shipping ??
    order?.shippingPrice ??
    order?.totals?.shipping ??
    order?.summary?.shipping ??
    0;
  const tax =
    order?.orderVatSum ??
    order?.tax ??
    order?.taxPrice ??
    order?.totals?.tax ??
    order?.summary?.tax ??
    0;
  const total =
    order?.orderSum ??
    order?.total ??
    order?.totalPrice ??
    order?.grandTotal ??
    order?.totals?.total ??
    order?.summary?.total ??
    subtotal + shipping + tax;

  const customer = order?.customer || order?.client || {};
  const userInfo = order?.userInfo || {};
  const nameFromUserInfo = (userInfo?.name?.first || userInfo?.name?.last)
    ? [userInfo?.name?.first, userInfo?.name?.last].filter(Boolean).join(" ")
    : [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(" ");
  const contactName =
    order?.contact?.name ||
    nameFromUserInfo ||
    customer?.name ||
    [customer?.firstName, customer?.lastName].filter(Boolean).join(" ").trim() ||
    order?.name ||
    null;
  const contactEmail =
    order?.contact?.email ||
    order?.email ||
    userInfo?.email ||
    customer?.email ||
    order?.customerEmail ||
    null;
  const contactPhone =
    order?.contact?.phone ||
    order?.phone ||
    userInfo?.mobilePhone ||
    userInfo?.phone ||
    customer?.phone ||
    order?.customerPhone ||
    null;

  const mapAddressInfo = (ai) =>
    ai
      ? {
          name:
            ai.name ||
            [ai.firstName, ai.lastName].filter(Boolean).join(" ") ||
            ai.company,
          postalCode: ai.postcode || ai.zip || ai.deliveryAreaPostcode,
          city: ai.city,
          address1: ai.line1 || ai.street || ai.address1,
          address2: ai.line2 || ai.address2 || ai.flatNumber,
          country: ai.country || ai.countryCode,
          phone: ai.phone,
        }
      : null;

  const mapShopifyAddress = (sa) =>
    sa
      ? {
          name:
            sa.name || [sa.first_name, sa.last_name].filter(Boolean).join(" "),
          postalCode: sa.zip,
          city: sa.city,
          address1: sa.address1,
          address2: sa.address2,
          country: sa.country || sa.country_code,
          phone: sa.phone,
        }
      : null;

  const shippingAddress =
    mapShopifyAddress(order?.shipping_address) ||
    mapAddressInfo(order?.addressInfo) ||
    order?.shippingAddress ||
    order?.deliveryAddress ||
    order?.customer?.shippingAddress ||
    null;

  const billingAddress =
    mapShopifyAddress(order?.billing_address) ||
    order?.billingAddress ||
    order?.invoiceAddress ||
    order?.customer?.billingAddress ||
    null;

  // Extended payment and shipping details
  const paymentTypes = Array.isArray(order?.paymentTypes)
    ? order.paymentTypes
    : (Array.isArray(order?.payments) ? order.payments : []);
  const paid =
    order?.isPayed === true ||
    order?.paid === true ||
    (typeof order?.financial_status === "string" && order.financial_status.toLowerCase() === "paid") ||
    (typeof order?.financialStatus === "string" && order.financialStatus.toLowerCase() === "paid");
  const cardType = order?.cardType || order?.card?.type || null;
  const courier = order?.courier || (order?.shipping_lines && order.shipping_lines[0]?.title) || (order?.shippingLines && order.shippingLines[0]?.title) || null;
  const fulfillments = order?.additionalData?.order?.fulfillments || order?.fulfillments || [];
  const primaryFulfillment = Array.isArray(fulfillments) ? fulfillments[0] : null;
  const trackingCompany = primaryFulfillment?.tracking_company || primaryFulfillment?.trackingCompany || null;
  const trackingNumber = primaryFulfillment?.tracking_number || primaryFulfillment?.trackingNumber || (primaryFulfillment?.tracking_numbers && primaryFulfillment.tracking_numbers[0]) || null;
  const trackingUrl = primaryFulfillment?.tracking_url || primaryFulfillment?.trackingUrl || (primaryFulfillment?.tracking_urls && primaryFulfillment.tracking_urls[0]) || null;

  // Icon mapping infos
  const platformInfo = order?.platform ? getPlatformInfo(order.platform) : null;
  const courierStringRaw = trackingCompany || courier || null;
  const courierInfo = courierStringRaw ? getCourierInfo(courierStringRaw) : null;

  // Payment status badge setup
  const paymentMethodStr = String(paymentMethod || "").toLowerCase().replace(/\s+/g, " ");
  const codKeywords = ["cod", "cash on delivery", "cash-on-delivery", "cashondelivery", "cash"];
  const hasCODInMethod = codKeywords.some(k => paymentMethodStr.includes(k));
  const hasCODInPayments = Array.isArray(paymentTypes) && paymentTypes.some(pt => {
    const t = String(pt?.paymentType || pt?.method || "").toLowerCase();
    return codKeywords.some(k => t.includes(k));
  });
  const isCOD = !paid && (hasCODInMethod || hasCODInPayments);
  const paymentStatusBadge = paid
    ? <Pill color="#0a7a2a" bg="rgba(10,122,42,0.12)" borderColor="rgba(10,122,42,0.35)">Paid</Pill>
    : isCOD
      ? <Pill color={ORANGE} bg="rgba(242,179,76,0.15)" borderColor="rgba(242,179,76,0.45)">Cash on delivery</Pill>
      : <Pill color="#b00020" bg="rgba(176,0,32,0.12)" borderColor="rgba(176,0,32,0.35)">Unpaid</Pill>;

  return (
    <Layout showHeader={false}>
        <Helmet>
            {/* Base */}
            <title>Order details at Happy Solutions</title>
            <meta name="description" content={`Order details page for order ${displayOrderCode}.`} />
            <link rel="canonical" href={"https://www.happysolutions.hu/?utm_source=orderPage&utm_id=" + encodeURIComponent(order?.orderCode || orderNumber)} />

            {/* Robots */}
            <meta name="googlebot" content="index,follow" />

            {/* Open Graph */}
            <meta property="og:locale" content={"en"} />
            <meta property="og:site_name" content={`Happy Solutions`} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={`Order ${displayOrderCode} - Happy Solutions`} />
            <meta property="og:description" content={`Order details page for order ${displayOrderCode}.`} />
            <meta property="og:url" content={"https://www.happysolutions.hu/?utm_source=facebook&utm_id=" + encodeURIComponent(order?.orderCode || orderNumber)} />
            <meta property="og:image" content={"https://images.happysolutions.hu/happy-share-image.jpg"} />
            <meta property="og:image:alt" content={"Happy Solutions ERP"} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />

            {/* PWA / UI */}
            <meta name="theme-color" content={ORANGE} />
            <link rel="icon" href="https://happygastro.hu/favicon.png" sizes="any" />
            <link rel="apple-touch-icon" href="https://happygastro.hu/favicon.png" />
        </Helmet>
      <div style={{ minHeight: "calc(100vh - 60px)", background: "#fff" }}>
        {/* Top bar */}
        <div style={{ background: ORANGE }}>
          <div style={{ maxWidth: 1180, margin: "0 auto", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <a href={`https://www.happysolutions.hu/?utm_source=orderPage${order?.orderCode ? `&utm_id=${encodeURIComponent(order.orderCode)}` : ''}`}
               target="_blank" rel="noreferrer" aria-label="Happy Solutions Home"
               style={{ display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
              <img src="https://happy-releases.fra1.cdn.digitaloceanspaces.com/Uploads/logo.png" alt="Happy Solutions" style={{ width: 34, height: 34, borderRadius: 6 }} />
              <span style={{ fontWeight: 900, color: "#111" }}>Happy Solutions</span>
            </a>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 26, lineHeight: 1.25, fontWeight: 900, color: "#111" }}>Order tracking</div>
              <div style={{ marginTop: 4, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 800, color: "#111" }}>Order: {displayOrderCode}</div>
                {status ? <Pill color="#333" bg="rgba(255,255,255,0.7)" borderColor="rgba(0,0,0,0.15)">{String(status)}</Pill> : null}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 16px 48px" }}>

          {loading && (
            <div style={{ padding: "16px 0", color: "#666" }}>Loading order details...</div>
          )}

          {!loading && error && (
            <div style={{ padding: 16, background: "#fff5f5", border: "1px solid #ffd3d3", borderRadius: 8, color: "#b00020" }}>
              {error}
            </div>
          )}

          {!loading && !error && order && (
            <>
              {/* Invoice download button */}
              {order?.invoicePDFURL ? (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                  <a
                    href={order.invoicePDFURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={(order?.invoiceNo || order?.invoiceData?.invoiceNumber || (order?.orderCode || String(orderId))) + ".pdf"}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 14px",
                      borderRadius: 12,
                      background: "#111",
                      color: "#fff",
                      textDecoration: "none",
                      fontWeight: 800,
                      boxShadow: "0 8px 22px rgba(0,0,0,0.15)",
                      border: "1px solid rgba(0,0,0,0.25)",
                    }}
                    aria-label="Számla letöltése"
                    title="Számla letöltése"
                  >
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
                        <path d="M14 3H6a2 2 0 0 0-2 2v16l3-2 3 2 3-2 3 2V8l-5-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14 3v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8 13h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8 17h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span>Számla letöltése</span>
                  </a>
                </div>
              ) : null}

              {/* Row 1: Order information + Contact informations */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
                <div style={CARD}>
                  <div style={CARD_TITLE}>Order information</div>
                  {order?.platform ? (
                    <Row label="Platform" value={<IconText icon={platformInfo?.icon} text={platformInfo?.text || String(order.platform)} />} />
                  ) : null}
                  {order?.orderFrom ? <Row label="Order from" value={String(order.orderFrom)} /> : null}
                  {createdAt ? <Row label="Created" value={new Date(createdAt).toLocaleString("en-GB")} /> : null}
                  {(courierInfo || courier) ? (
                    <Row label="Courier" value={<IconText icon={courierInfo?.icon} text={courierInfo?.text || String(courier)} />} />
                  ) : null}
                  {trackingNumber ? <Row label="Tracking number" value={trackingUrl ? <a href={trackingUrl} target="_blank" rel="noreferrer">{trackingNumber}</a> : trackingNumber} /> : null}
                </div>

                {(contactName || contactEmail || contactPhone) && (
                  <div style={CARD}>
                    <div style={CARD_TITLE}>Contact informations</div>
                    {contactName ? <Row label="Name" value={contactName} /> : null}
                    {contactEmail ? <Row label="Email" value={<a href={`mailto:${contactEmail}`}>{contactEmail}</a>} /> : null}
                    {contactPhone ? <Row label="Phone" value={<a href={`tel:${contactPhone}`}>{contactPhone}</a>} /> : null}
                  </div>
                )}
              </div>

              {/* Row 2: Payment details + Shipping address */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12, marginTop: 12 }}>
                <div style={CARD}>
                  <div style={CARD_TITLE}>Payment details</div>
                  <Row label="Payment method" value={String(paymentMethod)} />
                  <Row label="Paid" value={paymentStatusBadge} />
                  {cardType ? <Row label="Card type" value={String(cardType)} /> : null}
                  {Array.isArray(paymentTypes) && paymentTypes.length > 0 ? (
                    paymentTypes.map((pt, idx) => (
                      <Row key={idx} label={pt?.paymentType || pt?.method || "Payment"} value={<Price value={pt?.amount || pt?.value} currency={pt?.currency || currency} />} />
                    ))
                  ) : (
                    <Row label="Amount" value={<Price value={total} currency={currency} />} />
                  )}
                </div>

                {(shippingAddress || billingAddress) && (
                  <div style={CARD}>
                    {shippingAddress && (
                      <>
                        <div style={CARD_TITLE}>Shipping address</div>
                        {shippingAddress.name || shippingAddress.fullName ? <Row label="Name" value={shippingAddress.name || shippingAddress.fullName} /> : null}
                        {(shippingAddress.address1 || shippingAddress.address2) ? <Row label="Address" value={[shippingAddress.address1, shippingAddress.address2].filter(Boolean).join(", ")} /> : null}
                        {shippingAddress.city ? <Row label="City" value={shippingAddress.city} /> : null}
                        {shippingAddress.postalCode ? <Row label="Postal code" value={shippingAddress.postalCode} /> : null}
                        {shippingAddress.country ? <Row label="Country" value={shippingAddress.country} /> : null}
                        {shippingAddress.phone ? <Row label="Phone" value={<a href={`tel:${shippingAddress.phone}`}>{shippingAddress.phone}</a>} /> : null}
                      </>
                    )}
                    {billingAddress && (
                      <>
                        <div style={{ height: 12 }} />
                        <div style={CARD_TITLE}>Billing address</div>
                        <div style={{ whiteSpace: "pre-line", lineHeight: 1.4 }}>
                          {[billingAddress.name || billingAddress.fullName, [billingAddress.postalCode, billingAddress.city].filter(Boolean).join(" "), [billingAddress.address1, billingAddress.address2].filter(Boolean).join(" "), billingAddress.country, billingAddress.phone].filter(Boolean).join("\n")}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

            <div style={{ marginTop: 24 }}>
              <div style={{ border: `2px solid ${ORANGE}`, borderRadius: 10, overflow: "hidden" }}>
                <div style={{ padding: 12, background: ORANGE, color: "#111", fontWeight: 800 }}>Items</div>
                <div style={{ padding: 12 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#faf8f3" }}>
                        <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #eee", fontWeight: 700, color: "#444" }}>Product</th>
                        <th style={{ textAlign: "center", padding: "10px 12px", borderBottom: "1px solid #eee", fontWeight: 700, color: "#444", width: 80 }}>Qty</th>
                        <th style={{ textAlign: "right", padding: "10px 12px", borderBottom: "1px solid #eee", fontWeight: 700, color: "#444", width: 140 }}>Price</th>
                        <th style={{ textAlign: "right", padding: "10px 12px", borderBottom: "1px solid #eee", fontWeight: 700, color: "#444", width: 140 }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(items) && items.length > 0 ? (
                        items.map((it, idx) => <ItemRow key={idx} item={it} currency={currency} />)
                      ) : (
                        <tr>
                          <td colSpan={4} style={{ padding: 16, textAlign: "center", color: "#777" }}>No items</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div style={{ marginTop: 12, borderTop: "1px dashed #ddd", paddingTop: 12, float: 'right' }}>
                    <Row label="Subtotal" value={<Price value={subtotal} currency={currency} valueAligned={"right"}/>} />
                    {discount ? <Row label="Discount" value={<Price value={discount} currency={currency} valueAligned={"right"} />} /> : null}
                    {shipping ? <Row label="Delivery price" value={<Price value={shipping} valueAligned={"right"} currency={currency} />} /> : null}
                    {tax ? <Row label="Tax" value={<Price value={tax} currency={currency} valueAligned={"right"} />} /> : null}
                    <div style={{ height: 8 }} />
                    <Row label={<span style={{ fontWeight: 800 }}>Order sum</span>} value={<span style={{ fontWeight: 900, fontSize: 22 }}><Price valueAligned={"right"} value={total} currency={currency} /></span>} />
                  </div>
                </div>
              </div>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Compact footer */}
      <FooterOne compact orderCode={order?.orderCode} />
    </Layout>
  );
}
